import type { SetupGuide } from '@/features/setup-guide';
import type { ReactNode } from 'react';
import Adventure1CompletionCoverImage from '@/assets/images/adventure-1-cover.png';
import Adventure2CompletionCoverImage from '@/assets/images/adventure-2-cover.png';
import Adventure3CompletionCoverImage from '@/assets/images/adventure-3-cover.png';

import iconAudience from '@/assets/images/adventures/icon-audience.svg';
import iconAutomate from '@/assets/images/adventures/icon-automate.svg';
import iconCraft from '@/assets/images/adventures/icon-craft.svg';

import { UNDERSTAND_YOUR_AUDIENCE_STEPS } from './understand-your-audience';
import { AUTOMATE_WITH_CAMPAIGNS } from './automate-with-campaigns';
import { getSampleCampaign } from '@/features/campaign/api';
import { DESIGN_AN_EMAIL } from './design-an-email';
import { getSampleBroadcast } from '@/features/broadcasts/api';

type QueryFunction = (...args: any) => Promise<unknown>;

enum ROCKETS {
  UnderstandYourAudience = 'understand-your-audience', // Adventure 1
  AutomateWithCampaigns = 'automate-with-campaigns', // Adventure 2
  DesignAnEmail = 'design-an-email', // Adventure 3
}

export type RocketType = {
  id: ROCKETS;
  modelId:
    | 'people_tourtips_explored_at'
    | 'sample_workflow_explored_at'
    | 'sample_broadcast_explored_at'
    | 'people_rocket_completed_at';
  icon: string;
  title: string;
  steps: STEPS_TYPE;
  onInit?: (args?: any) => void;
  videoHref?: string;
  modal: {
    text: string;
    image: string;
    href: (t: any) => string;
  };
  productTourHref?: (workspaceId: number | null, setupGuide?: SetupGuide | null) => string;
};

export type RocketMeta = RocketType & { routes: string[] };

interface Step<T extends QueryFunction, U extends keyof SetupGuide> {
  id: string;
  content: ReactNode;
  ariaLabel: string;
  queryFn: T;
  // Completion function should return a number between 0 and 1,
  // representing the percentage completion of the step.
  // (less than 0 or greater than 1 will be clamped)
  completion: (args: Awaited<ReturnType<T>>) => number;
  modelId: U;
  tooltip?: string;
  href?: string | ((setupGuide: SetupGuide, currentPath: string | undefined) => string | null);
}

// Uses a function to create a step so that we can infer the
// return type of the query function for the completion function
// (using an object would require manually specifying the type)
function createStep<T extends QueryFunction, U extends keyof SetupGuide>(step: Step<T, U>) {
  return step;
}

// Select which config to load based on the activeRoute
function getRocketConfigByRoute(activeRoute: string | null | undefined): RocketMeta | null {
  if (activeRoute == null) {
    return null;
  }

  return CONFIGS.find((config) => config.routes.some((route) => activeRoute.indexOf(route) === 0)) ?? null;
}

function getRocketConfigById(id: string | null | undefined): RocketMeta | null {
  if (id == null) {
    return null;
  }

  return CONFIGS.find((config) => config.id === id) ?? null;
}

function getRocketIndex(id: ROCKETS | null | undefined) {
  if (id == null) {
    return null;
  }

  return CONFIGS.findIndex((c) => c.id === id);
}

/**
 * Note that the order of the Rockets here should correspond with the
 * order of the adventures in the UI repo, as the index is used in the copy
 */
const CONFIGS: RocketMeta[] = [
  {
    id: ROCKETS.UnderstandYourAudience,
    icon: iconAudience,
    modelId: 'people_rocket_completed_at',
    routes: ['people', 'segments'],
    modal: {
      text: 'First adventure complete! Complete all adventures to see the power of Customer.io.',
      href: (workspaceId) => `/workspaces/${workspaceId}/journeys/home`,
      image: Adventure1CompletionCoverImage,
    },
    title: 'Understand your audience',
    productTourHref: (workspaceId) => `/workspaces/${workspaceId}/journeys/tours/people`,
    videoHref: 'https://docs.customer.io/#16c466018fe27adc467e25d964226729-lightbox',
    steps: UNDERSTAND_YOUR_AUDIENCE_STEPS,
  },
  {
    id: ROCKETS.AutomateWithCampaigns,
    icon: iconAutomate,
    routes: ['campaigns', 'composer/actions'],
    modelId: 'sample_workflow_explored_at',
    title: 'Automate with Campaigns',
    onInit: getSampleCampaign,
    modal: {
      text: 'Adventure complete! Complete all adventures to see the power of Customer.io.',
      href: (workspaceId) => `/workspaces/${workspaceId}/journeys/home`,
      image: Adventure2CompletionCoverImage,
    },
    videoHref: 'https://docs.customer.io/journeys/create-a-campaign',
    productTourHref: (workspaceId) => `/workspaces/${workspaceId}/journeys/tours/campaigns`,
    steps: AUTOMATE_WITH_CAMPAIGNS,
  },
  {
    id: ROCKETS.DesignAnEmail,
    icon: iconCraft,
    modelId: 'sample_broadcast_explored_at',
    routes: ['broadcasts', 'composer/newsletters'],
    title: 'Design an Email',
    productTourHref: (workspaceId) => `/workspaces/${workspaceId}/journeys/tours/broadcasts`,
    onInit: getSampleBroadcast,
    modal: {
      text: 'Adventure complete! Complete all adventures to see the power of Customer.io.',
      href: (workspaceId) => `/workspaces/${workspaceId}/journeys/home`,
      image: Adventure3CompletionCoverImage,
    },
    steps: DESIGN_AN_EMAIL,
  },
];

export const ROCKET_STEPS_COMPLETION_MODEL_IDS = CONFIGS.map((config) => config.modelId);

export type STEPS_TYPE =
  | typeof UNDERSTAND_YOUR_AUDIENCE_STEPS
  | typeof AUTOMATE_WITH_CAMPAIGNS
  | typeof DESIGN_AN_EMAIL;

export { getRocketConfigByRoute, getRocketConfigById, getRocketIndex, ROCKETS, createStep, CONFIGS };
